import React, { useState, useCallback, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import SaveButton from '../SaveButton';
import Badge from './Badge';
import SmartImage from './SmartImage';
import ProductAnimation from './ProductAnimation';
import { getImageUrl, isVideoPath } from '../../utils/imageUrl';
import { formatPrice } from '../../utils/format';
import { iuteSku } from '../../utils/iutepay';
import IuteInstalments from '../IuteInstalments';
import { useCart } from '../../context/CartContext';
import { useSiteSettings } from '../../context/SiteSettingsContext';
import { ageLabel } from '../../utils/ageLabel';
import './ProductCard.css';

const NEW_THRESHOLD_DAYS = 14;

/**
 * Single source of truth for product cards across the storefront (home,
 * listing, category pages, related and recently viewed).
 *
 * Layout: the photo on a plate the colour of the studio backdrop, so a
 * product shot fills it edge to edge, with the product's looping clip
 * playing over it while the card is on screen (recommended age top-left,
 * favourite top-right); then the name, then price and a round add button. Category
 * and description are opt-in: a grid reads as one catalogue when every
 * card carries the same few facts.
 *
 * Variant controls density:
 *  - `default`  — home, listing, category pages
 *  - `compact`  — related / recently viewed on the product page (no add)
 */
const ProductCard = ({
  product,
  variant = 'default',
  showActions = true,
  showBadges = true,
  showCategory = false,
  showDescription = false,
  showQuickAdd = true,
  className = '',
}) => {
  const { i18n, t } = useTranslation();
  const cardRef = useRef(null);
  const { addToCart } = useCart();
  const { currency, currencySymbol, featureAgeRecommendations } = useSiteSettings();
  const navigate = useNavigate();
  const [added, setAdded] = useState(false);

  const hasVariants = product?.variants && product.variants.length > 0;
  const isOutOfStock = product?.stockStatus === 'out_of_stock';
  const canQuickAdd = showQuickAdd && variant !== 'compact' && !isOutOfStock;

  const handleQuickAdd = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    if (hasVariants) {
      // Variant products need option selection — send to PDP
      navigate(`/products/${product.slug}`);
      return;
    }
    if (added) return;
    addToCart(product, 1);
    setAdded(true);
    setTimeout(() => setAdded(false), 1800);
  }, [added, addToCart, hasVariants, navigate, product]);

  if (!product) return null;

  const heroImage = (product.images || []).find(Boolean);
  const isHeroVideo = isVideoPath(heroImage);
  const isNew =
    product.createdAt &&
    Date.now() - new Date(product.createdAt).getTime() <
      NEW_THRESHOLD_DAYS * 86400000;
  const lowStock = product.stockStatus === 'low_stock';
  // Parents shop by age first, so every card shows it on the photo.
  const age = featureAgeRecommendations ? ageLabel(t, product) : null;
  const href = `/products/${product.slug}`;

  return (
    <article ref={cardRef} className={`product-card-primitive product-card-primitive--${variant}${isOutOfStock ? ' is-sold-out' : ''} ${className}`}>
      <Link to={href} className="product-card-primitive__link">
        <div className="product-card-primitive__image-wrap">
          {heroImage ? (
            <SmartImage
              src={getImageUrl(heroImage, { size: isHeroVideo ? undefined : 'thumb' })}
              alt={product.name}
              name={product.name}
              imgClassName="product-card-primitive__image"
              onErrorFallback={getImageUrl(heroImage)}
              fit
            />
          ) : (
            <div className="product-card-primitive__placeholder" aria-hidden="true" />
          )}
          {product.animationUrl && (
            <ProductAnimation
              src={getImageUrl(product.animationUrl)}
              poster={heroImage && !isHeroVideo ? getImageUrl(heroImage, { size: 'thumb' }) : undefined}
              hostRef={cardRef}
            />
          )}
          {age && (
            <span className="product-card-primitive__age" aria-label={`${t('pdp.ageRangeLabel', 'Recommended age')}: ${age}`}>{age}</span>
          )}
          {showBadges && (isOutOfStock || lowStock || isNew) && (
            <span className="product-card-primitive__badges">
              {isOutOfStock
                ? <Badge variant="danger">{t('products.outOfStock', 'Out of stock')}</Badge>
                : lowStock
                  ? <Badge variant="warning">{t('products.lowStock', 'Low stock')}</Badge>
                  : <Badge variant="success">{t('products.badgeNew', 'New')}</Badge>}
            </span>
          )}
        </div>
        <div className="product-card-primitive__body">
          {showCategory && product.category?.name && (
            <span className="product-card-primitive__category">{product.category.name}</span>
          )}
          <h3 className="product-card-primitive__name">{product.name}</h3>
          {showDescription && product.descriptionShort && (
            <p className="product-card-primitive__desc">{product.descriptionShort}</p>
          )}
        </div>
      </Link>

      {showActions && (
        <div className="product-card-primitive__fav">
          <SaveButton productId={product.id} />
        </div>
      )}

      <div className="product-card-primitive__footer">
        <span className="product-card-primitive__price">
          {formatPrice(product.price, { lang: i18n.language })}
          <small> {currencySymbol || currency || 'USD'}</small>
        </span>
        {canQuickAdd && (
          <button
            type="button"
            className={`product-card-primitive__add${added ? ' is-added' : ''}`}
            onClick={handleQuickAdd}
            aria-label={added
              ? t('products.added', 'Added!')
              : `${hasVariants ? t('products.chooseOptions', 'Choose options') : t('products.quickAdd', 'Add to cart')}: ${product.name}`}
          >
            {added ? (
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.6" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><polyline points="20 6 9 17 4 12" /></svg>
            ) : (
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" aria-hidden="true"><path d="M12 5v14M5 12h14" /></svg>
            )}
          </button>
        )}
      </div>
      {variant !== 'compact' && !isOutOfStock && (
        <IuteInstalments amount={product.price} sku={iuteSku(product)} pageType="category" className="product-card-primitive__instalments" />
      )}
    </article>
  );
};

export default ProductCard;
